import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStoreConfig } from '@/contexts/StoreConfigContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Instagram, Facebook, Twitter } from 'lucide-react';
import PaymentIcons from '@/components/storefront/PaymentIcons';

export default function Footer() {
  const { config } = useStoreConfig();
  const { currency, setCurrency, currencies } = useCurrency();
  const [email, setEmail] = useState('');
  const social = config?.social || {};
  const contact = config?.contact || {};
  const storeName = config?.store_name || 'Florashop';
  const address = [contact.address, contact.city, contact.state, contact.zip, contact.country].filter(Boolean).join(', ');

  return (
    <footer data-testid="storefront-footer" style={{ backgroundColor: '#FDF2F0' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand + Newsletter */}
          <div>
            <h4 className="font-sans text-lg font-bold uppercase mb-3 tracking-wide" style={{ color: '#000' }}>{storeName}</h4>
            <p className="font-sans text-sm leading-relaxed mb-5" style={{ color: '#555' }}>
              {config?.description || 'Functional 3D-printed desk organizers, vases, planters, and sculptural accents for modern spaces.'}
            </p>
            <p className="font-sans text-xs font-semibold uppercase tracking-wider mb-2.5" style={{ color: '#000' }}>Join Our Newsletter</p>
            <div className="flex">
              <input data-testid="footer-newsletter-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 h-10 px-3 font-sans text-xs border border-r-0 outline-none transition-colors focus:border-[#E8545A]"
                style={{ borderColor: 'rgba(0,0,0,0.12)', backgroundColor: '#fff', color: '#000' }} />
              <button data-testid="footer-newsletter-submit"
                className="h-10 px-4 font-sans text-[10px] uppercase tracking-wider font-bold transition-colors"
                style={{ backgroundColor: '#000', color: '#fff' }}>
                Subscribe
              </button>
            </div>
            <div className="flex gap-4 mt-5">
              {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" style={{ color: '#555' }}><Instagram size={18} /></a>}
              {social.facebook && <a href={social.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" style={{ color: '#555' }}><Facebook size={18} /></a>}
              {social.twitter && <a href={social.twitter} target="_blank" rel="noopener noreferrer" data-testid="footer-twitter" style={{ color: '#555' }}><Twitter size={18} /></a>}
            </div>
          </div>

          {/* Information */}
          <div>
            <h4 className="font-sans text-xs font-bold uppercase tracking-wider mb-4" style={{ color: '#000' }}>Information</h4>
            <ul className="space-y-2.5">
              {[
                { to: '/privacy-policy', label: 'Privacy Policy' },
                { to: '/terms-of-service', label: 'Terms of Use' },
                { to: '/returns', label: 'Return & Refund Policy' },
                { to: '/shipping-policy', label: 'Shipping Policy' },
              ].map(l => (
                <li key={l.to}><Link to={l.to} className="font-sans text-sm transition-colors hover:text-[#000]" style={{ color: '#555' }}>{l.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Helpful Links */}
          <div>
            <h4 className="font-sans text-xs font-bold uppercase tracking-wider mb-4" style={{ color: '#000' }}>Helpful Links</h4>
            <ul className="space-y-2.5">
              {[
                { to: '/contact', label: 'Contact Us' },
                { to: '/track', label: 'Track My Order' },
                { to: '/shop', label: 'Shop All Products' },
                { to: '/cart', label: 'My Cart' },
              ].map(l => (
                <li key={l.to}><Link to={l.to} className="font-sans text-sm transition-colors hover:text-[#000]" style={{ color: '#555' }}>{l.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Customer Support */}
          <div>
            <h4 className="font-sans text-xs font-bold uppercase tracking-wider mb-4" style={{ color: '#000' }}>Customer Support</h4>
            <ul className="space-y-2.5">
              {contact.phone && <li className="font-sans text-sm" style={{ color: '#555' }}>{contact.phone}</li>}
              {contact.email && <li><a href={`mailto:${contact.email}`} className="font-sans text-sm transition-colors hover:text-[#000]" style={{ color: '#555' }}>{contact.email}</a></li>}
              {address && <li className="font-sans text-sm" style={{ color: '#555' }}>{address}</li>}
              <li className="font-sans text-sm" style={{ color: '#555' }}>Mon - Fri, 9am - 6pm</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-sans text-xs" style={{ color: '#888' }}>
            &copy; {new Date().getFullYear()} {storeName}. All rights reserved.
          </p>
          <div className="flex items-center gap-2">
            <select value={currency} onChange={e => setCurrency(e.target.value)}
              className="font-sans text-xs border rounded px-2 py-1 outline-none"
              style={{ borderColor: 'rgba(0,0,0,0.1)', backgroundColor: '#fff', color: '#333' }}>
              {currencies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <PaymentIcons size={34} />
          </div>
        </div>
      </div>
    </footer>
  );
}
